import { useState } from "react";
import { useReveal } from "../../lib/hooks";

type Item = {
  id: string;
  glyph: string;
  glyphLabel: string;
  title: string;
  body: string;
};

const ITEMS: Item[] = [
  {
    id: "what",
    glyph: "ज्ञान",
    glyphLabel: "jñāna",
    title: "What are the Vedas?",
    body: "The Vedas are the oldest scriptures of the Sanātana Dharma — humanity's most ancient spiritual texts. The word ‘Veda’ comes from the Sanskrit root ‘vid’, meaning ‘to know’. They represent the accumulated insight of ancient sages who perceived profound truths about existence, consciousness, and the cosmos.",
  },
  {
    id: "shruti",
    glyph: "श्रुति",
    glyphLabel: "śruti",
    title: "Śruti — that which is heard",
    body: "The Vedas are classified as Śruti, from the Sanskrit ‘śru’ — to hear. They are not authored: they are heard. Ancient sages in deep meditation became vessels for cosmic wisdom, and the eternal truths they perceived were transmitted as sound.",
  },
  {
    id: "transmission",
    glyph: "गुरु",
    glyphLabel: "guru",
    title: "An unbroken chain",
    body: "For thousands of years the Vedas were preserved entirely through oral transmission, teacher to student — the guru-śiṣya paramparā. Sophisticated memorisation techniques carried not just the words but the precise pronunciation, intonation, and rhythm across countless generations with extraordinary fidelity.",
  },
  {
    id: "four",
    glyph: "चत्वारि",
    glyphLabel: "chatvāri",
    title: "The Four Vedas",
    body: "The Vedic corpus is traditionally divided into four collections — Ṛgveda, Yajurveda, Sāmaveda, and Atharvaveda — each serving a unique purpose in spiritual practice and the pursuit of knowledge.",
  },
  {
    id: "living",
    glyph: "सनातन",
    glyphLabel: "sanātana",
    title: "A living tradition",
    body: "The Vedas are recognised by UNESCO as a Masterpiece of the Oral and Intangible Heritage of Humanity. Composed three to four millennia ago, they continue to be chanted today with the same precision — one of humanity's most remarkable feats in the preservation of knowledge.",
  },
];

export function Vedas() {
  const [openId, setOpenId] = useState<string | null>("what");
  const { ref } = useReveal<HTMLDivElement>();

  return (
    <section id="vedas" className="py-20 md:py-28">
      <div className="container-edit">
        <div className="section-heading">
          <span className="section-heading-num tabular">03 / The Vedas</span>
          <hr className="rule flex-1 hidden sm:block" />
        </div>

        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-16">
          {/* Left column — single sticky wrapper carries title + subtitle together */}
          <header className="col-span-12 lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <h2 className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[1.02] tracking-[-0.02em]">
                Discovering the{" "}
                <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
                  Vedas.
                </span>
              </h2>
              <p className="font-grotesk text-[0.78rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)] mt-8">
                Ancient wisdom &mdash; preserved through millennia
              </p>
            </div>
          </header>

          {/* Right column — accordion */}
          <div ref={ref} className="col-span-12 lg:col-span-7 reveal">
            {ITEMS.map((item) => {
              const open = openId === item.id;
              return (
                <div key={item.id} className="accordion-item">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : item.id)}
                    aria-expanded={open}
                    aria-controls={`acc-${item.id}`}
                    className="accordion-trigger"
                  >
                    <span className="flex items-baseline">
                      <span
                        className="accordion-trigger-deva font-deva"
                        aria-label={item.glyphLabel}
                      >
                        {item.glyph}
                      </span>
                      <span>{item.title}</span>
                    </span>
                    <span className="accordion-icon" aria-hidden>
                      +
                    </span>
                  </button>
                  <div
                    id={`acc-${item.id}`}
                    className="accordion-panel"
                    data-open={open}
                    role="region"
                  >
                    <div className="accordion-panel-inner">
                      <div className="accordion-panel-content">{item.body}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
